import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiAlertTriangle, FiCheckCircle, FiRotateCcw, FiX } from 'react-icons/fi';
import api from '../../libs/axios';

// Reopen a finished match (match-ops). Match 674: a chase closed after one ball
// left every later ball refused. Reopening has to put the INNINGS back too, so
// this shows exactly what will change before anything is written, and asks why.

interface TeamRef { id: number | null; name: string | null }
interface InningsRow {
  innings_number: number;
  batting_team: TeamRef;
  score: string;
  balls: number;
  is_completed: boolean;
  completed_at: string | null;
}
interface ReopenPlan {
  allowed: boolean;
  innings_number: number | null;
  blocked_reason: string | null;
  warnings: string[];
  auto_complete_possible: boolean;
}
interface Preview {
  match: {
    id: number;
    title: string | null;
    status: string;
    is_hidden: boolean;
    team_a: TeamRef;
    team_b: TeamRef;
    winning_team: TeamRef | null;
    result_summary: string | null;
  };
  innings: InningsRow[];
  refused_balls: { events: number; distinct_balls: number };
  plan: ReopenPlan;
}
interface ReopenResult {
  match_id: number;
  innings_reopened: number | null;
  auto_complete_armed: boolean;
  warnings: string[];
  next_steps: string[];
}

interface Props {
  matchId: string;
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || fallback;

const ReopenMatchModal: React.FC<Props> = ({ matchId, isOpen, onClose, onDone }) => {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [innings, setInnings] = useState<number | null>(null);
  const [autoComplete, setAutoComplete] = useState(true);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<ReopenResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async (requested: number | null) => {
    setLoading(true);
    setLoadError(null);
    try {
      const query = requested != null ? `?innings_number=${requested}` : '';
      const res: any = await api.get(`/match-ops/${matchId}/reopen-preview${query}`);
      const data: Preview = res.data;
      setPreview(data);
      if (requested == null) setInnings(data.plan.innings_number);
    } catch (e: any) {
      setPreview(null);
      setLoadError(errorMessage(e, 'Could not load this match.'));
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    if (!isOpen) return;
    setResult(null);
    setReason('');
    setAutoComplete(true);
    setInnings(null);
    load(null);
  }, [isOpen, load]);

  if (!isOpen) return null;

  const plan = preview?.plan;
  const reasonOk = reason.trim().length >= 5;
  const canSubmit = !!plan?.allowed && reasonOk && !submitting && !loading;

  const pickInnings = (n: number) => {
    setInnings(n);
    load(n);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res: any = await api.post(`/match-ops/${matchId}/reopen`, {
        reason: reason.trim(),
        innings_number: innings,
        auto_complete: autoComplete && !!plan?.auto_complete_possible,
      });
      setResult(res.data as ReopenResult);
      toast.success(`Match #${matchId} reopened`);
      onDone?.();
    } catch (e: any) {
      toast.error(errorMessage(e, 'Reopen failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const teamLabel = (t: TeamRef | null) => (t ? t.name || `Team ${t.id}` : '-');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={submitting ? undefined : onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl">
          <button
            onClick={onClose}
            disabled={submitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>

          <div className="p-6">
            <div className="flex items-center gap-3 mb-1">
              <FiRotateCcw className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-semibold text-gray-900">Reopen match #{matchId}</h3>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Sets the match back to live, clears its result and awards, and reopens one innings so the
              scorer's kept balls can be sent. The match leaves the points table until it is completed again.
            </p>

            {loading && !preview && <p className="text-sm text-gray-500">Loading match…</p>}
            {loadError && <p className="text-sm text-red-600">{loadError}</p>}

            {result ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 p-4">
                  <FiCheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="text-sm text-green-900">
                    <p className="font-medium">
                      Reopened{result.innings_reopened != null ? ` (innings ${result.innings_reopened})` : ''}.
                      {result.auto_complete_armed ? ' Automatic completion is armed.' : ''}
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Next steps</h4>
                  <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-700">
                    {result.next_steps.map((s) => <li key={s}>{s}</li>)}
                  </ol>
                </div>
                <div className="flex justify-end">
                  <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white text-sm">
                    Done
                  </button>
                </div>
              </div>
            ) : preview && plan ? (
              <div className="space-y-5">
                <div className="rounded-lg border border-gray-200 p-4 text-sm">
                  <p className="font-medium text-gray-900">
                    {teamLabel(preview.match.team_a)} v {teamLabel(preview.match.team_b)}
                    {preview.match.title ? <span className="text-gray-400 font-normal"> · {preview.match.title}</span> : null}
                  </p>
                  <p className="text-gray-600 mt-1">
                    Status <span className="font-medium capitalize">{preview.match.status.replace(/_/g, ' ')}</span>
                    {preview.match.result_summary ? <> · result "{preview.match.result_summary}"</> : null}
                    {preview.match.winning_team ? <> · winner {teamLabel(preview.match.winning_team)}</> : null}
                    {preview.match.is_hidden ? <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">hidden</span> : null}
                  </p>
                  {preview.refused_balls.distinct_balls > 0 && (
                    <p className="text-amber-700 mt-2">
                      The server refused {preview.refused_balls.distinct_balls} ball(s) for this match after it was closed.
                      The scorer&apos;s phone may hold more: balls queued behind a refused one are not sent, so the
                      app&apos;s &quot;balls not on server&quot; count is the one to go by.
                    </p>
                  )}
                </div>

                {preview.innings.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Innings to reopen</h4>
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <th className="w-10 px-3 py-2" />
                            <th className="text-left font-medium px-3 py-2">Innings</th>
                            <th className="text-left font-medium px-3 py-2">Batting</th>
                            <th className="text-right font-medium px-3 py-2">Score</th>
                            <th className="text-right font-medium px-3 py-2">Balls</th>
                            <th className="text-left font-medium px-3 py-2">State</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.innings.map((r) => (
                            <tr key={r.innings_number} className="border-t border-gray-100">
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="radio"
                                  name="reopen-innings"
                                  checked={innings === r.innings_number}
                                  onChange={() => pickInnings(r.innings_number)}
                                  disabled={loading || submitting}
                                />
                              </td>
                              <td className="px-3 py-2">{r.innings_number >= 3 ? `Super over (${r.innings_number})` : r.innings_number}</td>
                              <td className="px-3 py-2">{teamLabel(r.batting_team)}</td>
                              <td className="px-3 py-2 text-right">{r.score}</td>
                              <td className="px-3 py-2 text-right">{r.balls}</td>
                              <td className="px-3 py-2">{r.is_completed ? 'closed' : 'open'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!plan.allowed && plan.blocked_reason && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                    <FiAlertTriangle className="w-4 h-4 mt-0.5" />
                    <span>{plan.blocked_reason}</span>
                  </div>
                )}
                {plan.allowed && plan.warnings.length > 0 && (
                  <ul className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-1">
                    {plan.warnings.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                )}

                <label className={`flex items-start gap-2 text-sm ${plan.auto_complete_possible ? 'text-gray-800' : 'text-gray-400'}`}>
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={autoComplete && plan.auto_complete_possible}
                    disabled={!plan.auto_complete_possible || submitting}
                    onChange={(e) => setAutoComplete(e.target.checked)}
                  />
                  <span>
                    Complete the match automatically once the recovered balls finish the chase (target reached, overs
                    bowled or all out), with the normal result and awards.
                    {!plan.auto_complete_possible && ' Only available when reopening innings 2.'}
                  </span>
                </label>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1" htmlFor="reopen-reason">
                    Reason <span className="font-normal text-gray-500">(kept in the audit log)</span>
                  </label>
                  <textarea
                    id="reopen-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="e.g. Chase closed after one ball; organiser confirmed play continued"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={submitting}
                  />
                  {!reasonOk && reason.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">At least 5 characters.</p>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    disabled={submitting}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submit}
                    disabled={!canSubmit}
                    className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Reopening…' : `Reopen match #${matchId}`}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReopenMatchModal;
